import { Component, computed, inject, Input, signal, OnInit } from '@angular/core';
import { FormControl, FormBuilder } from '@angular/forms';
import { debounceTime, distinctUntilChanged, filter, finalize, delay } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UdtService } from '../../../shared/services/udt.service';
import { MediatorService } from 'src/app/shared/services/mediator.service';
import { DataSavedMessage } from 'src/app/shared/models/mediator-messages.model';
import { SignalRService, PeriforOnChangeMessages } from 'src/app/shared/services/signal-r.service';
import { UdtViewModel } from 'src/app/wellbore-inputs/models/undisturbed-temperature.model';
import { Subject } from 'rxjs';
import { StorageKeys, StoreService } from 'src/app/core/services/store.service';
import { UserUnitsModel } from 'src/app/core/components/user-units/user-units.model';
import { isEqual } from 'lodash-es';
import { extractValidationErrors } from 'src/app/shared/helpers/validation-error.helper';

/**
 * UDT Component (V2) - Simplified to match prototype pattern
 * 
 * No complex transformers or input$/output$ - just simple form controls
 * and manual save logic. Much more reliable for mode switching.
 */
@Component({
    selector: 'app-undisturbed-temperature-v2',
    templateUrl: './undisturbed-temperature-v2.component.html',
    providers: [],
    standalone: false
})
export class UndisturbedTemperatureV2Component implements OnInit {
    private udtService = inject(UdtService);
    private messenger = inject(MediatorService);
    private signalRService = inject(SignalRService);
    private fb = inject(FormBuilder);

    public userUnits: UserUnitsModel | null;
    public depthUnit: string;
    public tempUnit: string;

    @Input() componentId: string = '';

    // ViewModel for template (child components need this for validation rules)
    viewModel = signal<UdtViewModel | null>(null);

    // Save feedback signals
    saving = signal(false);
    saveSuccess = signal(false);
    saveError = signal(false);
    validationErrors = signal<string[]>([]);
    allValidationErrors = computed(() => {
        const combined = [...this.validationErrors()]
            .map(error => (error ?? '').trim())
            .filter(error => error.length > 0);
        return Array.from(new Set(combined));
    });

    // Trigger for temperature interpolator refresh
    refreshInterpolator$ = new Subject<void>();

    private isInitializing = true;
    private lastSavedDto: any | null = null;
    private suppressRefreshUntil = 0;

    private isDefinedValue(value: unknown): boolean {
        return value !== null && value !== undefined && value !== '';
    }

    private isCompleteCustomPoint(point: any): boolean {
        return this.isDefinedValue(point?.trueVerticalDepth)
            && this.isDefinedValue(point?.temperature);
    }

    private getCompleteCustomPointsFromForm(): Array<{ trueVerticalDepth: number; temperature: number }> {
        const customData = this.form.value.customPoints;
        const customPoints = customData?.customPoints ?? [];

        return customPoints
            .filter((point: any) => this.isCompleteCustomPoint(point))
            .map((point: any) => ({
                trueVerticalDepth: Number(point.trueVerticalDepth),
                temperature: Number(point.temperature)
            }));
    }

    private hasDepthOrderViolation(points: Array<{ trueVerticalDepth: number }>): boolean {
        let previousDepth: number | null = null;

        for (const point of points) {
            if (previousDepth !== null && point.trueVerticalDepth <= previousDepth) {
                return true;
            }

            previousDepth = point.trueVerticalDepth;
        }

        return false;
    }

    // Simple form - child components manage their own internal structure
    form = this.fb.group({
        constantGradient: new FormControl<any>(null),
        customPoints: new FormControl<any>(null)
    });

    // Track which mode is active - use getter/setter for ngModel binding
    private _isConstantGradient = signal<boolean>(true);

    get isConstantGradient(): boolean {
        return this._isConstantGradient();
    }

    set isConstantGradient(value: boolean) {
        const oldValue = this._isConstantGradient();
        this._isConstantGradient.set(value);

        // Save when mode changes (but not during initialization)
        // Use setTimeout to let CVA propagate child form values first
        if (!this.isInitializing && value !== oldValue) {
            // When switching to Custom mode, auto-generate if no data exists
            if (!value) {
                const vm = this.viewModel();
                const hasCustomPoints = vm?.custom.data.customPoints && vm.custom.data.customPoints.length > 0;
                if (!hasCustomPoints) {
                    this.autoGenerateCustomPoints();
                    return;
                }
            }

            setTimeout(() => this.save(), 0);
        }
    }

    constructor(private _storeService: StoreService) {
        // Auto-save on form changes with 300ms debounce
        this.form.valueChanges
            .pipe(
                takeUntilDestroyed(),
                debounceTime(450),
                distinctUntilChanged((previous, current) => isEqual(previous, current)),
                filter(() => {
                    // Don't save if initializing, invalid, already saving, or form values are null
                    const formValue = this.form.value;
                    const isGradientMode = this._isConstantGradient();
                    const hasData = isGradientMode
                        ? formValue.constantGradient !== null
                            : formValue.customPoints !== null;

                    return !this.isInitializing
                        && this.form.valid
                        && !this.saving()
                            && hasData
                            && this.canSaveCurrentMode();
                })
            )
            .subscribe(() => {
                this.save();
            });
    }

    async ngOnInit(): Promise<void> {
        // Initial data load
        this.loadData();

        this.userUnits = await this._storeService.get<UserUnitsModel>(StorageKeys.UNITS);
        this.depthUnit = this.userUnits ? this.userUnits.longLengths : 'ft';
        this.tempUnit = this.userUnits ? this.userUnits.temperature : '°F';

        // Listen for SignalR refresh events
        const hub = this.signalRService.getConnectionToNotificationHub();
        this.signalRService.subscribeToEventFilteredByDesignId(
            hub,
            SignalRService.ON_PFB_CHANGE,
            data => {
                if (this.saving()) {
                    return;
                }

                if (data.action === PeriforOnChangeMessages.REFRESH_UDT_INPUTS || data.action === PeriforOnChangeMessages.REFRESH_DATUMS) {
                    if (Date.now() < this.suppressRefreshUntil) {
                        return;
                    }
                    this.loadData();
                }

                if (data.action === PeriforOnChangeMessages.REFRESH_TRAJECTORY) {
                    this.loadData(true);
                }
            }
        );
    }

    private loadData(forceRefresh = false): void {
        this.isInitializing = true;
        this.udtService.getData(forceRefresh).subscribe(vm => {
            if (vm) {
                // Auto-generate custom points from gradient if in custom mode with no data
                const shouldAutoGenerate = !vm.isConstantGradient && (!vm.custom.data.customPoints || vm.custom.data.customPoints.length === 0);

                if (shouldAutoGenerate) {
                    this.autoGenerateCustomPoints();
                    return;
                }

                // Store ViewModel for template (child components need validation rules)
                this.viewModel.set(vm);
                this._isConstantGradient.set(vm.isConstantGradient);

                // Patch the form with the snapshot data so child CVAs can sync properly
                this.form.patchValue({
                    constantGradient: vm.constantGradient.data,
                    customPoints: vm.custom.data
                }, { emitEvent: false });

                this.lastSavedDto = {
                    isConstantGradient: vm.isConstantGradient,
                    constantGradient: vm.constantGradient.data,
                    custom: vm.custom.data
                };

                // Disable initialization flag after a short delay
                setTimeout(() => this.isInitializing = false, 500);
            }
        });
    }

    private save(): void {
        if (this.form.invalid || this.saving() || !this.canSaveCurrentMode()) return;

        const vm = this.viewModel();
        if (!vm) return;

        const dto = this.buildSaveDto(vm);
        if (this.lastSavedDto && isEqual(dto, this.lastSavedDto)) {
            return;
        }

        this.saving.set(true);
        this.saveSuccess.set(false);
        this.saveError.set(false);
        this.suppressRefreshUntil = Date.now() + 2500;

        this.udtService.updateUndisturbedTemperature(dto)
            .pipe(
                delay(100),
                finalize(() => this.saving.set(false))
            )
            .subscribe({
                next: () => {
                    this.lastSavedDto = JSON.parse(JSON.stringify(dto));
                    this.suppressRefreshUntil = Date.now() + 2500;
                    this.saveSuccess.set(true);
                    this.validationErrors.set([]); // Clear save-side validation errors
                    this.messenger.publish(new DataSavedMessage('UDT'));
                    this.refreshInterpolator$.next(); // Trigger interpolator refresh
                    setTimeout(() => this.saveSuccess.set(false), 2000);
                },
                error: (err) => {
                    console.error('❌ Save failed:', err);
                    this.saveError.set(true);

                    // Extract validation errors from ASP.NET Core response
                    const errors = extractValidationErrors(err);
                    this.validationErrors.set(errors);

                    setTimeout(() => {
                        this.saveError.set(false);
                        this.validationErrors.set([]);
                    }, 5000);
                }
            });
    }

    private buildSaveDto(vm: UdtViewModel): any {
        const formValue = this.form.value;
        const isGradientMode = this._isConstantGradient();
        const customData = formValue.customPoints || vm.custom.data;

        const filteredCustomData = {
            ...customData,
            customPoints: (customData?.customPoints ?? []).filter((point: any) => this.isCompleteCustomPoint(point))
        };

        return {
            isConstantGradient: isGradientMode,
            constantGradient: formValue.constantGradient || vm.constantGradient.data,
            custom: filteredCustomData
        };
    }

    private countCompleteCustomPoints(): number {
        return this.getCompleteCustomPointsFromForm().length;
    }

    private canSaveCurrentMode(): boolean {
        if (this._isConstantGradient()) {
            return this.form.value.constantGradient !== null;
        }

        const completePoints = this.getCompleteCustomPointsFromForm();
        return completePoints.length >= 2 && !this.hasDepthOrderViolation(completePoints);
    }

    private clearCustomGrid(): void {
        const vm = this.viewModel();
        if (!vm) {
            return;
        }

        const clearedCustomData = { ...vm.custom.data, customPoints: [] };
        this.viewModel.set({
            ...vm,
            custom: {
                ...vm.custom,
                data: clearedCustomData
            }
        });

        this.form.patchValue({ customPoints: clearedCustomData }, { emitEvent: false });
        this.messenger.publish(new DataSavedMessage('UDT'));
    }

    private autoGenerateCustomPoints(): void {
        // Auto-generate without user feedback (silent operation on initial load)
        this.udtService.generateCustomPointsFromGradient()
            .pipe(finalize(() => {}))
            .subscribe({
                next: () => {
                    // Reload to get the generated data
                    this.loadData(true);
                },
                error: (err) => {
                    // If auto-generation fails, still load the existing data (empty points)
                    console.error('⚠️ Auto-generation of custom points failed:', err);
                    this.loadData(true);
                }
            });
    }

    handleResetCustomPoints(): void {
        this.saving.set(true);
        this.saveSuccess.set(false);
        this.saveError.set(false);
        this.clearCustomGrid();

        this.udtService.generateCustomPointsFromGradient()
            .pipe(finalize(() => this.saving.set(false)))
            .subscribe({
                next: () => {
                    this.loadData(true);
                    this.saveSuccess.set(true);
                    setTimeout(() => this.saveSuccess.set(false), 2000);
                },
                error: (err) => {
                    console.error('🔄 Failed to reset custom points:', err);
                    this.loadData(true);
                    this.saveError.set(true);
                    setTimeout(() => this.saveError.set(false), 3000);
                }
            });
    }
}
